import { fromJS, List, Map, Set } from 'immutable';
import moment from 'moment';

import * as ActionType from '../actions/userManage';
import config from '../config';
import getErrorResponse from '../utils/getErrorResponse';
import { getModuleSuggestions, getSupervisorSuggestions } from '../utils/userSuggestions';

const defaultState = fromJS({
  users: [],
  suggestionUsers: [],
  filters: {
    includeInactive: false,
    search: '',
    categoryTitle: '',
    roleId: '',
    module: '',
    moduleId: '',
    supervisor: '',
    supervisorId: '',
    sort: '',
    sortOrder: '',
  },
  selectedUser: {},
  originalSelectedUser: {},
  formMode: 'view',
  userLoading: false,
  formError: {
    userName: '',
    module: '',
    email: '',
    rePassword: '',
  },
  roles: [],
  dashboardUserTitles: [],
  saveStatus: 'enabled',
  titleSuggestionMode: config.VIEW_MODE,
  moduleSuggestionMode: config.VIEW_MODE,
  supervisorSuggestionMode: config.VIEW_MODE,
  suggestions: [],
  titlesSuggestions: [],
  errors: [],
  reports: [],
  isMailingBacklog: false,
});

export default function(state = defaultState, action) {
  switch (action.type) {
    case ActionType.FETCHING_USERS:
      return state.merge({
        userLoading: true,
        users: List(),
        errors: [],
      });

    case ActionType.FETCHED_USERS_SUCCESS:
      return state.merge({
        users: getUsersFromResponse(state.get('users'), action.response),
        userLoading: false,
        errors: [],
      });

    case ActionType.FETCHED_USERS_FAILURE:
      return state.merge({
        userLoading: false,
        errors: ['Error in loading users'],
      });

    case ActionType.FETCHED_SUGGESTION_USERS_SUCCESS:
      return state.set(
        'suggestionUsers',
        sortByUserName(getUsersFromResponse(state.get('suggestionUsers'), action.response)),
      );

    case ActionType.SET_USER_LIST_FILTERS:
      return state.set('filters', action.filters);

    case ActionType.FETCHED_USER_DETAILS_SUCCESS: {
      const selectedUser = mapUser(action.response.data);

      return validateSelectedUser(state.merge({ selectedUser, originalSelectedUser: selectedUser, errors: [] }));
    }

    case ActionType.CLEAR_SELECTED_USER:
      return validateSelectedUser(state.merge({ selectedUser: {}, originalSelectedUser: {}, errors: [] }));

    case ActionType.UPDATE_SELECTED_USER:
      return validateSelectedUser(updateSelectedUser(state, action));

    case ActionType.CHANGE_MODE:
      return validateSelectedUser(
        state.merge({
          formMode: action.mode,
          selectedUser: action.mode === 'view' ? defaultState.get('selectedUser') : state.get('selectedUser'),
          errors: [],
        }),
      );

    case ActionType.FETCHED_ROLES_SUCCESS:
      return state.set('roles', getListFromResponse(action.response));

    case ActionType.FETCHED_DASHBOARD_USER_TITLES_SUCCESS:
      return state.set('dashboardUserTitles', getListFromResponse(action.response));

    case ActionType.SAVING_USER:
      return state.merge({
        saveStatus: 'saving',
        errors: [],
      });

    case ActionType.SAVED_USER_FAILURE:
      return state.merge({
        saveStatus: 'enabled',
        errors: getErrorResponse(action.response),
      });

    case ActionType.SAVED_USER_SUCCESS:
      return state.merge({
        saveStatus: 'enabled',
        originalSelectedUser: state.get('selectedUser'),
        errors: [],
      });

    case ActionType.UPDATE_TITLE_FIELD:
      return state.mergeDeep(
        fromJS({
          selectedUser: { title: action.title, titleId: action.titleId },
          titleSuggestionMode: config.VIEW_MODE,
          moduleSuggestionMode: config.VIEW_MODE,
          supervisorSuggestionMode: config.VIEW_MODE,
        }),
      );

    case ActionType.UPDATE_MODULE_FIELD:
      return validateSelectedUser(
        state.mergeDeep(
          fromJS({
            selectedUser: { module: action.module, moduleId: action.moduleId },
            moduleSuggestionMode: config.VIEW_MODE,
            supervisorSuggestionMode: config.VIEW_MODE,
            titleSuggestionMode: config.VIEW_MODE,
          }),
        ),
      );

    case ActionType.UPDATE_SUPERVISOR_FIELD:
      return validateSelectedUser(
        state.mergeDeep(
          fromJS({
            selectedUser: {
              supervisor: action.supervisor,
              supervisorId: action.supervisorId,
            },
            moduleSuggestionMode: config.VIEW_MODE,
            supervisorSuggestionMode: config.VIEW_MODE,
            titleSuggestionMode: config.VIEW_MODE,
          }),
        ),
      );

    case ActionType.UPDATE_LOGIN_ATTEMPTS:
      return updateLoginAttempts(state, action.checked);

    case ActionType.CLOSE_SUGGESTION:
      return validateSelectedUser(resetModuleSupervisor(state));

    case ActionType.FETCHED_REPORT_SUCCESS:
      return state.set('reports', fromJS(action.response.data));

    case ActionType.LOADED_TITLES: {
      return state.mergeDeep(fromJS({ titlesSuggestions: action.response.data }));
    }

    case ActionType.CLOSE_TITLE_SUGGESTION:
      return state.merge({
        titleSuggestionMode: config.VIEW_MODE,
        suggestions: List(),
      });

    case ActionType.OPEN_DROPDOWN_MENU_TITLES:
      return state.merge({
        titleSuggestionMode: config.SUGGEST_MODE,
        suggestions: getTitleSuggestions(state.get('titlesSuggestions'), ''),
      });

    default:
      return state;
  }
}

/**
 * Get users in response.
 *
 * @param {Immutable.List} currentUsers Users to keep if the request was not successful.
 * @param {object} response Response.
 * @returns {Immutable.List} List of users in the order returned by the server.
 */
const getUsersFromResponse = (currentUsers, response) => {
  if (response.status === 'success') {
    return List(response.data.map(mapListUser));
  }

  return currentUsers;
};

/**
 * Get role name and slug of a user.
 *
 * @param {object} user Returned user.
 * @returns {{ role: string, roleName: string }} Role slug and name of the first user role.
 */
const getUserRole = user => {
  const [firstRole] = user.roles || [];

  return {
    role: firstRole ? firstRole.slug : '',
    roleName: firstRole ? firstRole.name : '',
  };
};

/**
 * Map returned user list item (short user data) to processed user.
 *
 * @param {object} user Returned user.
 * @returns {Immutable.Map} Mapped user with data for the table and suggestions.
 */
const mapListUser = user => {
  const { role, roleName } = getUserRole(user);

  return Map({
    id: user.id,
    userName: user.userName,
    officePhoneExt: user.officePhoneExt,
    email: user.email,
    inactive: user.inactive,
    fullName: `${user.lastName}, ${user.firstName}`,
    supervisor: user.supervisor || '',
    module: user.module || '',
    roleName,
    role,
  });
};

/**
 * Map returned user to processed user.
 *
 * @param {object} user Returned user.
 * @return {Immutable.Map} Mapped user with comfortable data.
 */
function mapUser(user) {
  if (!user) return;

  const { role, roleName } = getUserRole(user);
  const fullName = `${user.lastName}, ${user.firstName}`;
  const supervisor = user.supervisor || '';
  const supervisorId = user.supervisorId || 0;
  const module = user.module || '';
  const moduleId = user.moduleId || 0;
  const title = user.title || '';
  const titleId = user.titleId || 0;
  const dashboardUserTitleId = user.dashboardUserTitleId || '';
  const reportIds = Set(user.reports.map(r => r.id));
  const hireDate = user.hireDate && moment(user.hireDate);
  const startDate = user.startDate && moment(user.startDate);
  const endDate = user.endDate && moment(user.endDate);
  const browseAccesses = Set(user.browseAccesses.map(item => item.id));

  return Map({
    id: user.id,
    userName: user.userName,
    officePhoneExt: user.officePhoneExt,
    dialpadUserId: user.dialpadUserId,
    firefliesApiToken: user.firefliesApiToken,
    firefliesWebhookUrl: user.firefliesWebhookUrl,
    shareFirefliesMeetings: Boolean(user.shareFirefliesMeetings),
    officePhone: user.officePhone,
    directDial: user.directDial,
    cellPhone: user.cellPhone,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    inactive: user.inactive,
    fullName,
    supervisor,
    supervisorId,
    module,
    moduleId,
    roleName,
    role,
    reportIds,
    isMailingBacklog: user.isMailingBacklog,
    hireDate,
    startDate,
    endDate,
    title,
    titleId,
    dashboardUserTitleId,
    browseAccesses,
  });
}

/**
 * Sort users' list by userName.
 *
 * @param {Immutable.List} users List of user.
 * @return {Immutable.List} Sorted users list.
 */
function sortByUserName(users) {
  return users.sort((a, b) => a.get('userName').localeCompare(b.get('userName')));
}

/**
 * Get a list of items from a successful API response.
 *
 * @return {Immutable.List} Items.
 */
function getListFromResponse(response) {
  if (response.status === 'success') {
    return fromJS(response.data);
  }

  return List();
}

/**
 * Validate selected user.
 *
 * @param {object} state Current state.
 * @return New state.
 */
function validateSelectedUser(state) {
  const { id, userName, module, password, role, rePassword, dashboardUserTitleId } = state.get('selectedUser').toJS();

  // Field validators
  // An erased password is an empty string, while the untouched one is undefined.
  const isPasswordsNotMatch = (password || '') !== (rePassword || '');

  // Validation messages
  const userNameMessage = !userName ? 'Please input user name' : '';
  const moduleMessage = !module ? 'Please input module' : '';
  const passwordMessage = !id && !password ? 'Please input password' : '';
  const roleMessage = !role ? 'Please select a role' : '';
  const rePasswordMessage = isPasswordsNotMatch ? "Password doesn't match" : '';
  const dashboardUserTitleIdMessage = !dashboardUserTitleId ? 'Please select a title category' : '';

  // Result error
  const formError = Map({
    userName: userNameMessage,
    module: moduleMessage,
    password: passwordMessage,
    role: roleMessage,
    rePassword: rePasswordMessage,
    dashboardUserTitleId: dashboardUserTitleIdMessage,
  });

  // Error status
  const saveStatus = formError.filter(error => error.length > 0).size > 0 ? 'error' : 'enabled';

  return state.merge({
    formError,
    saveStatus,
  });
}

/**
 * Validate selected user.
 *
 * @param state {Object} State.
 * @param checked {Boolean} Checkbox state.
 * @returns New state.
 */
function updateLoginAttempts(state, checked) {
  if (checked) {
    return state.setIn(['selectedUser', 'incorrectLoginAttempts'], 0);
  }

  return state.set('selectedUser', state.get('selectedUser').delete('incorrectLoginAttempts'));
}

/**
 * Get suggestion list of titles.
 *
 * @param titles {Immutable.List} List of titles.
 * @param value {String} Checkbox state.
 */
function getTitleSuggestions(titles, value) {
  const inputValue = value.trim().toLowerCase();
  const inputLength = inputValue.length;

  return titles
    .toJS()
    .filter(title => title.toLowerCase().slice(0, inputLength) === inputValue)
    .map((title, index) => Map({ id: index, name: title }));
}

/**
 * Validate selected user.
 *
 * @param state {Object} State.
 * @param action {Object} Action.
 * @returns New state.
 */
function updateSelectedUser(state, action) {
  const newState = state.merge({
    suggestions: [],
    errors: [],
  });

  if (action.name === 'title') {
    return updateSelectedUserTitle(newState, action.value);
  }

  if (action.name === 'module') {
    return updateSelectedUserModule(newState, action.value);
  }

  if (action.name === 'supervisor') {
    return updateSelectedUserSupervisor(newState, action.value);
  }

  if (action.name === 'reports') {
    let reports = state.getIn(['selectedUser', 'reportIds'], Set());

    reports = action.checked ? reports.add(parseInt(action.value, 10)) : reports.remove(parseInt(action.value, 10));

    return newState
      .mergeDeep({
        titleSuggestionMode: config.VIEW_MODE,
        supervisorSuggestionMode: config.VIEW_MODE,
        moduleSuggestionMode: config.VIEW_MODE,
      })
      .setIn(['selectedUser', 'reportIds'], reports);
  }

  if (action.name === 'accesses') {
    let accesses = state.getIn(['selectedUser', 'browseAccesses'], Set());

    accesses = action.checked ? accesses.add(parseInt(action.value, 10)) : accesses.remove(parseInt(action.value, 10));

    return newState
      .mergeDeep({
        titleSuggestionMode: config.VIEW_MODE,
        supervisorSuggestionMode: config.VIEW_MODE,
        moduleSuggestionMode: config.VIEW_MODE,
      })
      .setIn(['selectedUser', 'browseAccesses'], accesses);
  }

  if (action.name === 'dashboardUserTitleId') {
    const dashboardUserTitleId = action.value ? parseInt(action.value, 10) : '';

    return newState
      .mergeDeep({
        titleSuggestionMode: config.VIEW_MODE,
        supervisorSuggestionMode: config.VIEW_MODE,
        moduleSuggestionMode: config.VIEW_MODE,
      })
      .setIn(['selectedUser', 'dashboardUserTitleId'], dashboardUserTitleId);
  }

  if (action.name === 'role') {
    let keys = [];
    let accesses = new Set();

    if ([config.DIRECTOR, config.FINANCE_ADMIN, config.ADMINISTRATOR].includes(action.value)) {
      keys = config.browse.get('accesses').map(item => parseInt(item.get('key'), 10));
    } else if (
      [config.RESEARCH_INTERN, config.JUNIOR_ANALYST, config.ANALYST_ASSOCIATE, config.PROCESS_MANAGER].includes(
        action.value,
      )
    ) {
      keys = config.browse
        .get('accesses')
        .filter(item => item.get('name') !== 'buyers')
        .map(item => parseInt(item.get('key'), 10));
    }
    accesses = accesses.union(keys);

    return newState
      .mergeDeep({
        titleSuggestionMode: config.VIEW_MODE,
        supervisorSuggestionMode: config.VIEW_MODE,
        moduleSuggestionMode: config.VIEW_MODE,
      })
      .setIn(['selectedUser', 'role'], action.value)
      .setIn(['selectedUser', 'browseAccesses'], accesses);
  }

  return newState.mergeDeep({
    titleSuggestionMode: config.VIEW_MODE,
    supervisorSuggestionMode: config.VIEW_MODE,
    moduleSuggestionMode: config.VIEW_MODE,
    selectedUser: { [action.name]: action.value },
  });
}

/**
 * Update module field of selected user.
 *
 * @param state {Object} State.
 * @param value {String} Module value.
 */
function updateSelectedUserModule(state, value) {
  return state.mergeDeep({
    titleSuggestionMode: config.VIEW_MODE,
    moduleSuggestionMode: value ? config.SUGGEST_MODE : config.VIEW_MODE,
    supervisorSuggestionMode: config.VIEW_MODE,
    suggestions: getModuleSuggestions(state.get('suggestionUsers'), value),
    selectedUser: {
      module: value,
      moduleId: value ? state.getIn(['selectedUser', 'moduleId']) : null,
    },
  });
}

/**
 * Update title field of selected user.
 *
 * @param state {Object} State.
 * @param value {String} Title value.
 */
function updateSelectedUserTitle(state, value) {
  return state.mergeDeep({
    titleSuggestionMode: value ? config.SUGGEST_MODE : config.VIEW_MODE,
    supervisorSuggestionMode: config.VIEW_MODE,
    moduleSuggestionMode: config.VIEW_MODE,
    suggestions: getTitleSuggestions(state.get('titlesSuggestions'), value),
    selectedUser: {
      title: value,
      titleId: value ? state.getIn(['selectedUser', 'titleId']) : null,
    },
  });
}

/**
 * Update supervisor field of selected user.
 *
 * @param state {Object} State.
 * @param value {String} Supervisor value.
 */
function updateSelectedUserSupervisor(state, value) {
  return state.mergeDeep({
    titleSuggestionMode: config.VIEW_MODE,
    supervisorSuggestionMode: value ? config.SUGGEST_MODE : config.VIEW_MODE,
    moduleSuggestionMode: config.VIEW_MODE,
    suggestions: getSupervisorSuggestions(state.get('suggestionUsers'), value),
    selectedUser: {
      supervisor: value,
      supervisorId: value ? state.getIn(['selectedUser', 'supervisorId']) : null,
    },
  });
}

/**
 * If users don't select any suggestion, reset module and supervisor to previous value.
 *
 * @param state {Object} State.
 */
function resetModuleSupervisor(state) {
  const users = state.get('suggestionUsers');
  const moduleId = state.getIn(['selectedUser', 'moduleId']);
  const supervisorId = state.getIn(['selectedUser', 'supervisorId']);
  const lookedModule = lookupUser(users, moduleId);
  const lookedSupervisor = lookupUser(users, supervisorId);

  return state.mergeDeep({
    selectedUser: {
      module: lookedModule ? lookedModule.get('userName') : '',
      supervisor: lookedSupervisor ? lookedSupervisor.get('userName') : '',
    },
    supervisorSuggestionMode: config.VIEW_MODE,
    moduleSuggestionMode: config.VIEW_MODE,
    titleSuggestionMode: config.VIEW_MODE,
  });
}

/**
 * Lookup user.
 *
 * @param users {Immutable.List} List of users.
 * @param userId {Number} User's id.
 */
function lookupUser(users, userId) {
  if (userId) {
    return users.find(user => user.get('id') === userId);
  }

  return null;
}
